import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { navItems, siteInfo } from "@/mocks/site";

const formatDate = () => {
  const now = new Date();
  const week = ["日", "一", "二", "三", "四", "五", "六"][now.getDay()];
  return `${now.getFullYear()} 年 ${now.getMonth() + 1} 月 ${now.getDate()} 日 · 星期${week}`;
};

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const term = keyword.trim();
    setSearchOpen(false);
    if (term) {
      navigate(`/gossip?q=${encodeURIComponent(term)}`);
    }
  };

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header className="w-full">
      <div className="bg-foreground-950 text-background-100">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 md:px-8 py-2 text-[11px] md:text-xs">
          <span className="flex items-center gap-2">
            <i className="ri-calendar-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
            <time dateTime="2026-09-12">{formatDate()}</time>
          </span>
          <span className="hidden sm:flex items-center gap-2 text-background-200">
            <i className="ri-notification-3-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
            {siteInfo.updateTime}
          </span>
          <a
            className="flex items-center gap-1 text-accent-300 hover:text-accent-200 transition-colors whitespace-nowrap"
            href={`mailto:${siteInfo.tipEmail}`}
          >
            <i className="ri-send-plane-fill w-4 h-4 flex items-center justify-center" aria-hidden="true" />
            我要报料
          </a>
        </div>
      </div>

      <div className="bg-background-50 border-b border-background-200">
        <div className="flex items-center justify-between gap-4 px-4 md:px-8 py-4 md:py-5">
          <Link to="/" className="flex items-center gap-3 group" aria-label="黑料每日首页">
            <span className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-md bg-primary-500 text-background-50 font-heading text-2xl font-bold leading-none">
              黑
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-foreground-950">
                {siteInfo.name}
              </span>
              <span className="text-[11px] md:text-xs text-foreground-500 tracking-[0.2em] font-label">
                DAILY BRIEF
              </span>
            </span>
          </Link>

          <p className="hidden lg:block font-heading text-sm text-foreground-600 max-w-xs text-center">
            {siteInfo.slogan}
          </p>

          <div className="flex items-center gap-2">
            {searchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center gap-1">
                <input
                  autoFocus
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索瓜单关键词"
                  aria-label="搜索站内内容"
                  className="w-40 md:w-56 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
                <button
                  type="submit"
                  aria-label="提交搜索"
                  className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-search-line" aria-hidden="true" />
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label="打开搜索"
                className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-search-line" aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开菜单"
              aria-expanded={menuOpen}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-700 cursor-pointer"
            >
              <i className={menuOpen ? "ri-close-line" : "ri-menu-2-line"} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <nav aria-label="主导航" className="sticky top-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200">
        <div className="hidden md:flex items-center px-4 md:px-8">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`relative px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                isActive(item.path)
                  ? "text-primary-600"
                  : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              {item.label}
              {isActive(item.path) && (
                <span className="absolute left-4 right-4 -bottom-px h-0.5 bg-primary-500 rounded-full" />
              )}
            </Link>
          ))}
        </div>

        {menuOpen && (
          <div className="md:hidden flex flex-col px-4 py-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`py-3 text-sm border-b border-background-200 last:border-none ${
                  isActive(item.path) ? "text-primary-600 font-medium" : "text-foreground-700"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </nav>
    </header>
  );
}